import React from 'react';
import * as Sentry from '@sentry/react';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  gql,
  HttpLink,
  NormalizedCacheObject,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { useMaintenanceMode } from '@quality24/react-maintenance-mode';

import { Authenticator } from '@quality24/react-auth';

import { getCache } from '@/inpatient-dashboard/services/cache';
import { headers as projectHeaders } from '@/inpatient-dashboard/services/http';
import subscriptionClient from '@/inpatient-dashboard/services/websocket';

export const cache = getCache();

/**
 * This fetch is passed to the HTTPLink (apollo link)
 * for handling re-authorization.
 */
const fetcher = (uri: RequestInfo | URL, options: RequestInit | undefined) =>
  fetch(uri, {
    ...options,
    headers: { ...options?.headers, ...projectHeaders },
  }).then((response) => response);

// //////////////////////////////////////////////
// Link definitions
// //////////////////////////////////////////////

// Create the HTTP auth middleware
export const authLink = setContext(async (_ignore, { headers }) => {
  const session = await Authenticator.currentSession();
  if (!session || !session.getIdToken()) {
    return headers;
  }

  return {
    ...headers,
    headers: { authorization: `Bearer ${session.getIdToken().getJwtToken()}` },
  };
});

// Create WebSocket link
export const wsLink = new GraphQLWsLink(subscriptionClient);

// //////////////////////////////////////////////
// Types
// //////////////////////////////////////////////

export interface Props {
  children: React.ReactNode;
  apiURL: string;
}

const ExtendedApolloProvider: React.FunctionComponent<Props> = ({
  children,
  apiURL,
}) => {
  const { enableMaintenanceMode, isEnabled: isMaintenanceModeEnabled } =
    useMaintenanceMode();

  // Create an HTTP link
  const httpLink = React.useMemo(
    () =>
      new HttpLink({
        uri: apiURL,
        fetch: fetcher,
        // Batch configurations (need to replace HttpLink with BatchHttpLink)
        // batchMax value equal max api calls found on http://localhost:3006/modules/daily#quality
        // batchMax: 11,
        // Default batchInterval value
        // batchInterval: 20,
      }),
    [apiURL],
  );

  const errorLink = React.useMemo(
    () =>
      onError(({ graphQLErrors, networkError, forward, operation }) => {
        (async () => {
          // This refreshes the token
          const session = await Authenticator.currentSession();

          if (graphQLErrors) {
            graphQLErrors.forEach(async (error) => {
              if (error && 'statusCode' in error) {
                // Forbidden graphQL error inside Apollo response. Happens when authentication is not valid.
                if (error.statusCode === 403) {
                  await Authenticator.signOut();
                  return;
                }

                // Unauthorized graphQL error inside Apollo response. Happens on expired token.
                if (error.statusCode === 401) {
                  const newContext = {
                    ...operation.getContext(),
                    headers: {
                      authorization: `Bearer ${session
                        .getIdToken()
                        .getJwtToken()}`,
                    },
                  };

                  operation.setContext(newContext);
                  forward(operation);
                }
              }
            });
          }

          if (networkError && 'statusCode' in networkError) {
            // Forbidden graphQL error inside Apollo response. Happens when authentication is not valid.
            if (networkError.statusCode === 403) {
              await Authenticator.signOut();
              return;
            }

            // Unauthorized graphQL error inside Apollo response. Happens on expired token.
            if (networkError.statusCode === 401) {
              const newContext = {
                ...operation.getContext(),
                headers: {
                  authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
                },
              };

              operation.setContext(newContext);
              forward(operation);
            }
            // maintenance mode
            if (
              networkError.statusCode === 503 &&
              networkError.response.headers.has('Retry-After')
            ) {
              enableMaintenanceMode();
            }
          }
        })();
      }),
    [enableMaintenanceMode],
  );

  const client: ApolloClient<NormalizedCacheObject> = React.useMemo(
    () =>
      new ApolloClient({
        link: split(
          // Split based on operation type
          ({ query }) => {
            const definition = getMainDefinition(query);
            return (
              definition.kind === 'OperationDefinition' &&
              definition.operation === 'subscription'
            );
          },
          wsLink,
          ApolloLink.from([errorLink, authLink, httpLink]),
        ),
        cache,

        defaultOptions: {
          watchQuery: {
            fetchPolicy: 'network-only',
            nextFetchPolicy: 'cache-first',
          },
        },
      }),
    [errorLink, httpLink],
  );

  React.useEffect(() => {
    /**
     * Attach connection listeners
     */
    const unsubscribeConnectedListener = subscriptionClient.on(
      'connected',
      () => {
        // Reset apollo store to refetch all active queries. This side-effect is suggested in
        // https://github.com/apollographql/apollo-client/issues/1831#issuecomment-311442700
        client.reFetchObservableQueries();
        // update...
      },
    );

    const unsubscribeErrorListener = subscriptionClient.on('error', () => {
      console.info('WebSocket error.'); // eslint-disable-line no-console
      if (!isMaintenanceModeEnabled) {
        client
          .query({
            query: gql`
              query FetchApiVersion {
                apiVersion {
                  versionName
                }
              }
            `,
          })
          .catch((error) => {
            if (
              error?.networkError?.statusCode === 503 &&
              error?.networkError?.response?.headers.get('Retry-After')
            ) {
              enableMaintenanceMode();
            }
          });
      }
    });

    return () => {
      unsubscribeErrorListener();
      unsubscribeConnectedListener();
    };
  }, [apiURL, client, enableMaintenanceMode, isMaintenanceModeEnabled]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default Sentry.withProfiler(ExtendedApolloProvider);
